import { motion } from "framer-motion";
import { Gamepad2, Tv, Joystick, Cpu, Footprints, Dices, CupSoda, TreePine, Check } from "lucide-react";
import { CheckoutButton } from "@/components/CheckoutButton";
import { SodaBuilder } from "@/components/SodaBuilder";
import { useWaiver } from "@/components/Layout";
import { useContent, parseList, parseLines } from "@/lib/content";
import { PixelSprite } from "@/components/PixelSprites";

const STATIONS = [
  { icon: Tv, imgKey: "images.console_corner" },
  { icon: Joystick, imgKey: "images.arcade" },
  { icon: Cpu, imgKey: "images.couch_sim" },
  { icon: Footprints, imgKey: "images.vr" },
  { icon: Dices, imgKey: null },
];

export default function Lounge() {
  const openWaiver = useWaiver();
  const content = useContent();
  const stationRows = parseList(content["lounge.stations"]);
  const stations = STATIONS.map((s, i) => ({
    ...s, img: s.imgKey ? content[s.imgKey] : null,
    title: stationRows[i]?.[0] || "", desc: stationRows[i]?.[1] || "", meta: stationRows[i]?.[2] || "",
  }));
  const lh1 = (content["lounge.h1"] || "").split("|");
  const sh2 = (content["lounge.soda_h"] || "").split("|");
  const patioPoints = parseLines(content["lounge.patio_points"]);
  const passes = parseList(content["lounge.passes"]).filter((r) => r[3]).map((r) => ({ name: r[0], price: r[1], unit: r[2], key: r[3], features: (r[4] || "").split(";").filter(Boolean), popular: r[5] === "yes" }));
  const sodaMenu = parseList(content["lounge.soda_menu"]).map((r) => ({ name: r[0], desc: r[1], price: r[2] }));
  return (
    <div data-testid="lounge-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["lounge.eyebrow"]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl" data-testid="lounge-heading">
            {lh1[0]} <span className="text-neon-cyan glow-cyan">{lh1[1]}</span> {lh1[2]}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {content["lounge.intro"]}
          </p>
        </div>
      </section>

      {/* STATIONS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="stations-section">
        <div className="mb-10 flex items-center gap-3">
          <PixelSprite name="ghost" size={36} className="anim-bob" />
          <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["lounge.stations_h"]}</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {stations.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="card-hover relative overflow-hidden rounded-xl border border-white/10 bg-inkalt" data-testid={`station-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {s.img ? (
                <div className="crt relative h-44 overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-t from-inkalt to-transparent" />
                </div>
              ) : (
                <div className="flex h-44 items-center justify-center bg-gradient-to-br from-inkalt to-ink">
                  <s.icon className="h-14 w-14 text-neon-cyan/40" />
                </div>
              )}
              <div className="p-6">
                <s.icon className="mb-2 h-5 w-5 text-neon-cyan" />
                <h3 className="font-display text-lg font-extrabold uppercase text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-neon-gold">{s.meta}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="scroll-mt-24 border-y border-white/10 bg-inkalt/40" data-testid="pricing-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["lounge.pricing_eyebrow"]}</p>
              <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["lounge.pricing_h"]}</h2>
              <PixelSprite name="coin" size={34} className="anim-bob-slow" />
            </div>
            <button onClick={openWaiver} data-testid="lounge-sign-waiver-button"
              className="font-display text-xs font-bold uppercase tracking-wide text-neon-gold transition-colors hover:text-white">
              Sign waiver before you arrive →
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {passes.map((p) => (
              <div key={p.key} data-testid={`pass-card-${p.key}`}
                className={`card-hover relative flex flex-col rounded-xl border p-6 ${p.popular ? "border-neon-cyan/60 bg-neon-cyan/5" : "border-white/10 bg-inkalt"}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-4 rounded-full bg-neon-cyan px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-ink">Most Popular</span>
                )}
                <h3 className="font-display text-lg font-extrabold uppercase text-white">{p.name}</h3>
                <p className="mt-2"><span className="font-display text-4xl font-black text-white">{p.price}</span>
                  <span className="font-mono text-xs text-slate-400"> {p.unit}</span></p>
                <ul className="mt-4 flex-1 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon-green" /> {f}
                    </li>
                  ))}
                </ul>
                <CheckoutButton lookupKey={p.key} testId={`${p.key.replace(/_/g, "-")}-checkout-button`}
                  className={`mt-6 w-full rounded-md px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${p.popular ? "bg-neon-cyan text-ink hover:bg-white" : "border border-white/20 text-white hover:border-neon-cyan hover:text-neon-cyan"}`}>
                  Buy {p.name}
                </CheckoutButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SODA BAR */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="soda-bar-section">
        <div className="mb-10 grid items-center gap-8 lg:grid-cols-2">
          <div>
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-pink">{content["lounge.soda_eyebrow"]}</p>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
              {sh2[0]} <span className="text-neon-pink">{sh2[1]}</span>
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300">
              {content["lounge.soda_desc"]}
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {sodaMenu.map((s) => (
                <div key={s.name} className="rounded-lg border border-white/10 bg-inkalt p-4" data-testid={`soda-menu-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-display text-sm font-extrabold uppercase text-white">{s.name}</p>
                    <span className="font-mono text-sm text-neon-gold">{s.price}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-xl border border-white/10">
            <img src={content["images.lounge_soda"]} alt="Dirty sodas at the bar" loading="lazy" className="h-full min-h-[320px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md bg-ink/80 px-3 py-2 backdrop-blur">
              <CupSoda className="h-4 w-4 text-neon-pink" />
              <span className="font-mono text-xs text-slate-200">{content["lounge.soda_badge"]}</span>
            </div>
          </div>
        </div>
        <SodaBuilder />
      </section>

      {/* PATIO */}
      <section className="border-t border-white/10 bg-inkalt/40" data-testid="patio-section">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-xl border border-white/10">
            <img src={content["images.patio"]} alt="The Game Room patio at dusk" loading="lazy" className="h-full min-h-[300px] w-full object-cover" />
          </div>
          <div>
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-green">{content["lounge.patio_eyebrow"]}</p>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["lounge.patio_h"]}</h2>
            <ul className="mt-6 space-y-3">
              {patioPoints.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                  <TreePine className="mt-0.5 h-4 w-4 shrink-0 text-neon-green" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-3">
              <Gamepad2 className="h-5 w-5 text-neon-cyan" />
              <p className="font-mono text-xs text-slate-400">{content["lounge.patio_note"]}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
